/**
 * Static example data for Strategic Research Blueprint showcase
 * Topic: "Should a mid-size B2B SaaS company expand into the European market?"
 */

import type {
  StrategicStreamingState, StrategicWorkstream, StrategicConflict,
  StressTestResult, DebateArgument, MediatorConsensus, GhostDeckSlide,
  StrategicAgent,
} from '@/hooks/useStrategicStream';

const EXAMPLE_QUERY = 'Should a mid-size B2B SaaS company expand into the European market?';

function buildWorkstreams(): StrategicWorkstream[] {
  return [
    {
      id: 'ws-market',
      name: 'Market Opportunity Assessment',
      keyQuestions: [
        'What is the total addressable market for B2B SaaS in Europe?',
        'Which verticals show highest growth and lowest competitive density?',
        'What are the entry barriers specific to European markets?',
      ],
      agents: [
        {
          workstreamId: 'ws-market', agentIndex: 0, agentRole: 'Market Researcher',
          model: 'Claude 4 Sonnet', status: 'complete' as const,
          outputPreview: 'European B2B SaaS market reached EUR 72B in 2025, growing at 18% CAGR. The DACH region (Germany, Austria, Switzerland) represents the largest single opportunity at EUR 21B, followed by UK at EUR 18B and Nordics at EUR 9B. Key insight: mid-market SaaS penetration in Germany is only 34% vs 67% in the US, suggesting significant whitespace...',
          fullOutput: 'European B2B SaaS market reached EUR 72B in 2025, growing at 18% CAGR. The DACH region (Germany, Austria, Switzerland) represents the largest single opportunity at EUR 21B, followed by UK at EUR 18B and Nordics at EUR 9B.\n\nKey insight: mid-market SaaS penetration in Germany is only 34% vs 67% in the US, suggesting significant whitespace. However, European enterprise buyers have longer sales cycles (avg 6.2 months vs 3.8 months in US) and stronger preference for on-premise/hybrid deployment options.\n\nVertical analysis: Healthcare IT and FinTech SaaS show highest growth (24% and 22% CAGR respectively) with relatively low competitive density from US entrants.',
          keyFindings: ['EUR 72B market growing at 18% CAGR', 'DACH region is largest opportunity (EUR 21B)', 'Mid-market penetration gap: 34% vs 67% US'],
          sources: [
            { url: 'https://gartner.com/eu-saas-2025', title: 'Gartner European SaaS Market Forecast', type: 'web' as const },
            { url: null, title: 'IDC European Cloud Tracker Q3 2025', type: 'citation' as const, citationNumber: 1 },
          ],
          latencySeconds: 11.2, citationCount: 8,
        },
        {
          workstreamId: 'ws-market', agentIndex: 1, agentRole: 'Consumer Psychologist',
          model: 'GPT-4.1', status: 'complete' as const,
          outputPreview: 'European B2B buyers exhibit distinct behavioral patterns that differ significantly from US counterparts. Trust-building requires an average of 4.7 touchpoints before trial adoption vs 2.3 in the US. Brand reputation and local references carry 3x the weight of feature comparisons. The "Mittelstand" effect in Germany...',
          fullOutput: 'European B2B buyers exhibit distinct behavioral patterns that differ significantly from US counterparts. Trust-building requires an average of 4.7 touchpoints before trial adoption vs 2.3 in the US.\n\nBrand reputation and local references carry 3x the weight of feature comparisons. The "Mittelstand" effect in Germany means mid-market companies prioritize long-term vendor relationships over best-of-breed technology selection.\n\nData sovereignty is a dealbreaker for 68% of DACH enterprise buyers. Companies that cannot demonstrate EU-based data residency are immediately disqualified from procurement processes. This is not just a compliance requirement but a deeply held cultural value around data privacy.\n\nKey behavioral differences:\n- Decision-making involves 4-6 stakeholders vs 2-3 in US\n- Proof-of-concept requirements are more rigorous (avg 45 days vs 14 days)\n- Annual contract preference is 78% vs 52% in US\n- Price sensitivity is lower but value justification requirements are higher',
          keyFindings: ['4.7 touchpoints needed before trial vs 2.3 in US', 'Local references carry 3x weight vs features', 'Data sovereignty is a dealbreaker for 68% of DACH buyers'],
          sources: [
            { url: null, title: 'Forrester: European B2B Buying Behavior 2025', type: 'citation' as const, citationNumber: 2 },
          ],
          latencySeconds: 9.8, citationCount: 6,
        } as StrategicAgent,
      ],
      status: 'complete' as const,
      synthesisPreview: 'European B2B SaaS market presents a EUR 72B opportunity with significant mid-market penetration gaps. DACH region is the primary beachhead. Success requires adapting to longer sales cycles, trust-heavy buying behavior, and data sovereignty requirements.',
      keyFindings: ['EUR 72B market at 18% CAGR', 'DACH optimal beachhead', 'Penetration gap creates whitespace', 'Longer sales cycles (6.2 vs 3.8 months)'],
      totalCitations: 14,
      latencySeconds: 21.0,
    },
    {
      id: 'ws-regulatory',
      name: 'Regulatory & Compliance Landscape',
      keyQuestions: [
        'What are the GDPR and data sovereignty implications?',
        'How do EU AI Act requirements affect SaaS products with AI features?',
        'What entity structure optimizes for tax and regulatory compliance?',
      ],
      agents: [
        {
          workstreamId: 'ws-regulatory', agentIndex: 0, agentRole: 'Operations Analyst',
          model: 'Gemini 2.5 Pro', status: 'complete' as const,
          outputPreview: 'GDPR compliance is a necessary but insufficient condition for European market entry. Beyond data protection, companies must navigate: (1) EU AI Act classification - most B2B SaaS with AI features will fall under "limited risk" requiring transparency obligations, (2) Digital Markets Act implications if platform features are integrated...',
          fullOutput: 'GDPR compliance is a necessary but insufficient condition for European market entry. Beyond data protection, companies must navigate:\n\n1. **EU AI Act Classification** - Most B2B SaaS with AI features will fall under "limited risk" requiring transparency obligations. This means disclosing AI-generated content, maintaining human oversight documentation, and publishing technical documentation about AI system capabilities.\n\n2. **Digital Markets Act** - If platform features are integrated with any designated gatekeeper services, additional interoperability and data portability requirements apply.\n\n3. **Data Residency Requirements** - 73% of enterprise buyers in the EU require data to be stored within EU borders. This necessitates EU-based cloud infrastructure (AWS eu-central-1, Azure West Europe, or GCP europe-west3).\n\n4. **Entity Structure Recommendation** - Irish HoldCo with subsidiaries in Germany and Netherlands provides optimal tax treatment (12.5% Irish corporate rate) while maintaining regulatory compliance across key markets.\n\n5. **Compliance Timeline** - Full GDPR + EU AI Act compliance typically requires 4-6 months of preparation, including DPO appointment, DPIA completion, and AI system documentation.',
          keyFindings: ['GDPR compliance is table stakes, not differentiator', 'EU AI Act: most B2B SaaS = limited risk tier', 'Data residency in EU required by 73% of enterprise buyers'],
          sources: [
            { url: 'https://ec.europa.eu/ai-act', title: 'EU AI Act Official Text', type: 'web' as const },
          ],
          latencySeconds: 10.5, citationCount: 7,
        } as StrategicAgent,
      ],
      status: 'complete' as const,
      synthesisPreview: 'Regulatory landscape is complex but navigable. GDPR compliance is baseline. EU AI Act adds transparency requirements for AI-powered features. Recommended entity structure: Irish HoldCo with subsidiaries in Germany and Netherlands for optimal tax treatment and regulatory positioning.',
      keyFindings: ['GDPR is baseline requirement', 'EU AI Act: limited risk classification for most SaaS', 'Irish HoldCo recommended for tax optimization'],
      totalCitations: 7,
      latencySeconds: 10.5,
    },
    {
      id: 'ws-gtm',
      name: 'Go-to-Market Strategy',
      keyQuestions: [
        'What is the optimal market entry sequence (which countries first)?',
        'Should we build a local team or use channel partners?',
        'What pricing adjustments are needed for European markets?',
      ],
      agents: [
        {
          workstreamId: 'ws-gtm', agentIndex: 0, agentRole: 'Product Strategist',
          model: 'Claude 4 Sonnet', status: 'complete' as const,
          outputPreview: 'Recommended phased entry: Phase 1 (months 0-6): UK market as English-language beachhead with lowest cultural adaptation cost. Phase 2 (months 6-18): DACH expansion with localized product (German UI, local data centers, DACH-specific compliance features). Phase 3 (months 18-30): Nordics and Benelux as high-GDP...',
          fullOutput: 'Recommended phased entry strategy for European market:\n\n**Phase 1 (Months 0-6): UK Market Launch**\nUK serves as the English-language beachhead with the lowest cultural adaptation cost. Key advantages: shared language, similar business culture, strong SaaS adoption rates (highest in Europe at 72%). Initial team: 3-5 people (Country Manager, 2 AEs, 1 CSM, 1 Solutions Engineer).\n\n**Phase 2 (Months 6-18): DACH Expansion**\nFull localization required: German UI, local data centers (Frankfurt), DACH-specific compliance features. The German mid-market ("Mittelstand") represents the largest revenue opportunity but requires significant cultural adaptation. Hire local sales team with existing enterprise relationships.\n\n**Phase 3 (Months 18-30): Nordics and Benelux**\nHigh-GDP, English-proficient markets with strong digital adoption. Lower localization costs than DACH. Can leverage UK team for initial coverage before dedicated hires.\n\n**Distribution Model:**\nHybrid approach recommended - direct enterprise sales for deals >$50K ARR, channel partners for mid-market. Partner with 2-3 established European IT consultancies (e.g., Capgemini, Accenture local offices) for credibility and pipeline generation.',
          keyFindings: ['UK first as English-language beachhead', 'DACH in Phase 2 with full localization', 'Hybrid model: direct sales + channel partners'],
          sources: [],
          latencySeconds: 10.1, citationCount: 5,
        } as StrategicAgent,
        {
          workstreamId: 'ws-gtm', agentIndex: 1, agentRole: 'Financial Modeler',
          model: 'GPT-4.1', status: 'complete' as const,
          outputPreview: 'Pricing analysis indicates European willingness-to-pay is 15-20% below US levels for equivalent SaaS products, but contract durations are 40% longer on average (2.1 years vs 1.5 years). Recommended pricing strategy: maintain US price points but offer annual commitment discounts of 20-25%...',
          fullOutput: 'Pricing analysis indicates European willingness-to-pay is 15-20% below US levels for equivalent SaaS products, but contract durations are 40% longer on average (2.1 years vs 1.5 years).\n\n**Recommended Pricing Strategy:**\n- Maintain US price points but offer annual commitment discounts of 20-25%\n- Price in EUR (not USD conversion) to eliminate currency friction\n- Offer multi-year contracts with 30% discount for 3-year commitments\n\n**Revenue Per Customer Analysis:**\nDespite lower WTP, net revenue per customer is nearly equivalent over lifetime value due to longer contracts and lower churn (European B2B SaaS churn averages 8% vs 12% in US).\n\n**Pricing Tiers for European Market:**\n- Starter: EUR 49/user/month (vs $59 US)\n- Professional: EUR 99/user/month (vs $119 US)\n- Enterprise: Custom pricing, typically EUR 150-250/user/month\n\n**Key Insight:** European buyers are less price-sensitive but more value-sensitive. ROI documentation and case studies are critical for procurement approval processes.',
          keyFindings: ['WTP 15-20% below US but contracts 40% longer', 'Net revenue per customer nearly equivalent over LTV', 'EUR pricing required (not USD conversion)'],
          sources: [],
          latencySeconds: 8.7, citationCount: 4,
        } as StrategicAgent,
      ],
      status: 'complete' as const,
      synthesisPreview: 'Phased market entry recommended: UK (0-6mo) -> DACH (6-18mo) -> Nordics (18-30mo). Hybrid distribution model with direct enterprise sales and channel partners for mid-market. Price in EUR with annual commitment discounts to offset lower WTP while capturing longer contract durations.',
      keyFindings: ['Phased entry: UK -> DACH -> Nordics', 'Hybrid distribution model', 'EUR pricing with commitment discounts'],
      totalCitations: 9,
      latencySeconds: 18.8,
    },
  ];
}

function buildFinancialWorkstream(): StrategicWorkstream {
  return {
    id: 'ws-financial',
    name: 'Financial Modeling & ROI',
    keyQuestions: [
      'What is the expected investment required for European expansion?',
      'What is the projected payback period and ROI?',
      'How does European expansion affect overall company valuation?',
    ],
    agents: [
      {
        workstreamId: 'ws-financial', agentIndex: 0, agentRole: 'Financial Modeler',
        model: 'Gemini 2.5 Pro', status: 'complete' as const,
        outputPreview: 'Base case financial model: Year 1 investment of $4.2M (team hiring: $2.1M, infrastructure: $0.8M, legal/compliance: $0.6M, marketing: $0.7M). Revenue projection: Year 1: $1.8M, Year 2: $5.4M, Year 3: $12.1M. Breakeven at month 22. IRR of 47% over 5-year horizon...',
        fullOutput: 'Base case financial model for European expansion:\n\n**Year 1 Investment Breakdown: $4.2M**\n- Team hiring: $2.1M (Country Manager, 4 AEs, 2 CSMs, 1 SE, 1 Marketing)\n- Infrastructure: $0.8M (EU data centers, local office, tools)\n- Legal/Compliance: $0.6M (GDPR, EU AI Act, entity setup, legal counsel)\n- Marketing: $0.7M (events, content localization, demand gen)\n\n**Revenue Projections:**\n- Year 1: $1.8M (20 customers, $90K avg ACV)\n- Year 2: $5.4M (55 customers, $98K avg ACV)\n- Year 3: $12.1M (110 customers, $110K avg ACV)\n\n**Key Financial Metrics:**\n- Breakeven: Month 22\n- IRR: 47% over 5-year horizon\n- NPV: $18.7M at 12% discount rate\n- Payback period: 2.8 years\n\n**Sensitivity Analysis:**\n- Downside (30% revenue miss): Breakeven at month 31, IRR 28%\n- Upside (20% revenue beat): Breakeven at month 18, IRR 62%\n- Compliance cost 2x: Breakeven at month 25, IRR 38%\n\n**Currency Risk:** USD/EUR exposure of approximately $3M annually by Year 3. Recommend hedging strategy using forward contracts for 60% of projected EUR revenue.',
        keyFindings: ['$4.2M Year 1 investment', 'Breakeven at month 22', '47% IRR over 5 years', 'NPV of $18.7M at 12% discount rate'],
        sources: [],
        latencySeconds: 12.3, citationCount: 5,
      } as StrategicAgent,
    ],
    status: 'complete' as const,
    synthesisPreview: 'European expansion requires $4.2M Year 1 investment with projected breakeven at month 22. Base case IRR of 47% and NPV of $18.7M. Downside scenario (30% revenue miss) still achieves breakeven by month 31.',
    keyFindings: ['$4.2M investment required', 'Month 22 breakeven', '47% IRR base case', 'Resilient under downside stress'],
    totalCitations: 5,
    latencySeconds: 12.3,
  };
}

function buildConflicts(): StrategicConflict[] {
  return [
    {
      conflictId: 'conflict-1',
      workstreamA: 'Market Opportunity Assessment',
      workstreamB: 'Go-to-Market Strategy',
      findingA: 'DACH region is the largest market opportunity (EUR 21B) and should be the primary focus',
      findingB: 'UK should be the Phase 1 beachhead due to lower cultural adaptation costs and English language advantage',
      severity: 'significant',
      description: 'Market size analysis points to DACH as the primary opportunity, but GTM strategy recommends UK first due to lower entry barriers. This sequencing tension could delay revenue capture in the largest market.',
      resolution: 'UK-first sequencing is correct for risk mitigation despite DACH being the larger opportunity. English-language launch reduces localization costs and builds European credibility. DACH preparation should run in parallel during Phase 1 to minimize delay.',
      resolutionRationale: 'The 6-month UK phase allows product-market fit validation at lower cost while DACH localization runs in parallel. Net delay to DACH revenue is only 3-4 months vs a direct DACH entry, but risk of failure is significantly reduced.',
      resolved: true,
    },
    {
      conflictId: 'conflict-2',
      workstreamA: 'Regulatory & Compliance Landscape',
      workstreamB: 'Financial Modeling & ROI',
      findingA: 'EU AI Act compliance will require significant product modifications and ongoing compliance monitoring',
      findingB: 'Financial model allocates only $0.6M for legal/compliance in Year 1, which may be insufficient',
      severity: 'critical',
      description: 'Regulatory workstream identifies substantial EU AI Act compliance burden, but financial model may underestimate associated costs. If compliance costs are 2-3x the estimate, payback period extends significantly.',
      resolution: 'Financial model revised upward to $1.1M for compliance in Year 1, extending breakeven to month 25 (from 22). Recommendation: hire a dedicated EU compliance officer and engage specialized AI Act advisory firm in pre-launch phase.',
      resolutionRationale: 'Sensitivity analysis shows that even at $1.5M compliance cost, the project IRR remains above 35% hurdle rate. The compliance investment also creates competitive moat as smaller competitors cannot afford equivalent compliance infrastructure.',
      resolved: true,
    },
  ];
}

function buildStressTestResults(): StressTestResult[] {
  return [
    {
      assumptionId: 'st-1',
      assumptionStatement: 'European B2B SaaS market will continue growing at 18% CAGR through 2028',
      testResult: 'validated',
      evidenceFor: [
        'IDC, Gartner, and Forrester all project 15-20% CAGR through 2028',
        'Enterprise digital transformation spend in Europe accelerating post-COVID',
        'EU Digital Decade targets mandate 75% cloud adoption by 2030',
      ],
      evidenceAgainst: [
        'European economic slowdown could reduce IT budgets by 5-10%',
        'Market saturation in UK and Nordics may drag overall CAGR below 15%',
      ],
      confidence: 0.82,
      critique: 'Growth rate is well-supported by multiple analyst sources. Downside risk is moderate but unlikely to drop below 12% CAGR even in recessionary scenario.',
    },
    {
      assumptionId: 'st-2',
      assumptionStatement: 'DACH mid-market SaaS penetration gap (34% vs 67% US) represents accessible whitespace',
      testResult: 'validated',
      evidenceFor: [
        'German Mittelstand digital transformation initiatives backed by government subsidies',
        'Successful case studies: Personio (HR SaaS) and Celonis (Process Mining) demonstrate DACH appetite',
      ],
      evidenceAgainst: [
        'Gap partly reflects cultural preference for on-premise solutions, not unmet demand',
        'German data sovereignty requirements create friction for cloud adoption',
      ],
      confidence: 0.68,
      critique: 'The gap is real but not entirely addressable. Approximately 40-50% of the gap reflects genuine whitespace; the remainder reflects structural preference for hybrid/on-premise. Product may need hybrid deployment option.',
    },
    {
      assumptionId: 'st-3',
      assumptionStatement: 'Breakeven achievable within 24 months of market entry',
      testResult: 'inconclusive',
      evidenceFor: [
        'Financial model shows month 22 breakeven under base case assumptions',
        'Comparable US SaaS companies entering Europe achieved breakeven in 18-30 months',
      ],
      evidenceAgainst: [
        'Longer European sales cycles (6.2 months) could delay initial revenue by 2-3 months',
        'Compliance cost underestimate identified in cross-workstream conflict analysis',
        'Currency risk (USD/EUR) not fully hedged in financial model',
      ],
      confidence: 0.55,
      critique: 'Breakeven timeline is achievable but highly sensitive to sales cycle assumptions and compliance costs. Revised model with higher compliance costs shows month 25 breakeven. Recommendation: plan for 30-month breakeven as conservative base.',
    },
  ];
}

function buildDebateArguments(): DebateArgument[] {
  return [
    {
      debaterRole: 'Senior Analyst', debaterName: 'Strategic Analyst',
      roundNumber: 1, position: 'Expand Now',
      argument: 'The European expansion window is time-sensitive. Three major US SaaS competitors have announced European expansion plans for 2026. First-mover advantage in the DACH mid-market could be worth $50M+ in incremental ARR over 5 years. The financial model shows strong ROI even under conservative assumptions.',
      evidenceCited: ['Market sizing data (EUR 72B)', 'Competitor intelligence', 'Financial model IRR of 47%'],
      counterPoints: ['Timing risk is real but manageable with phased approach'],
      confidence: 0.78, timestamp: Date.now() - 120000,
    },
    {
      debaterRole: 'Managing Partner', debaterName: 'Risk Assessor',
      roundNumber: 1, position: 'Proceed with Caution',
      argument: 'The opportunity is compelling, but execution risk is substantial. The compliance cost underestimate, longer sales cycles, and cultural adaptation requirements suggest a more conservative phased approach. I recommend a "toe in the water" strategy: start with UK-only for 12 months to validate assumptions before committing to DACH investment.',
      evidenceCited: ['Compliance cost gap', 'Sales cycle data (6.2 months)', 'Stress test results'],
      counterPoints: ['Over-caution could mean losing the DACH window to competitors'],
      confidence: 0.72, timestamp: Date.now() - 100000,
    },
    {
      debaterRole: 'Senior Analyst', debaterName: 'Strategic Analyst',
      roundNumber: 2, position: 'Phased Expansion (Revised)',
      argument: 'I accept the phased approach but propose an acceleration: UK launch immediately with DACH preparation running in parallel. Pre-sell to 3-5 German enterprise prospects during the UK phase to validate demand before committing full DACH investment. This balances speed with risk management.',
      evidenceCited: ['UK-first GTM recommendation', 'Parallel DACH preparation feasibility'],
      counterPoints: [],
      confidence: 0.85, timestamp: Date.now() - 80000,
    },
    {
      debaterRole: 'Managing Partner', debaterName: 'Risk Assessor',
      roundNumber: 2, position: 'Agreed - Phased with Validation Gates',
      argument: 'The parallel preparation model is sound. I recommend adding explicit validation gates: (1) UK must achieve 20 paying customers within 9 months to trigger DACH launch, (2) Pre-sell pipeline in Germany must show 5+ qualified leads, (3) Compliance framework must be fully certified. These gates protect against downside while preserving optionality.',
      evidenceCited: ['Comparable expansion playbooks', 'Gate-based investment frameworks'],
      counterPoints: [],
      confidence: 0.88, timestamp: Date.now() - 60000,
    },
  ];
}

const MEDIATOR_CONSENSUS: MediatorConsensus = {
  consensusReached: true,
  winningPosition: 'Phased Expansion with Validation Gates',
  confidence: 0.86,
  keyAgreements: [
    'European expansion is strategically warranted by EUR 72B market opportunity',
    'UK-first phased approach minimizes risk while preserving DACH optionality',
    'Parallel DACH preparation during UK phase is essential to maintain competitive timing',
    'Explicit validation gates (20 UK customers, 5 DACH leads) should govern phase transitions',
    'Compliance budget should be revised upward to $1.1M+ for Year 1',
  ],
  unresolvedTensions: [
    'Exact timing of DACH launch depends on UK phase performance - could be 6 or 12 months',
    'Hybrid deployment option for DACH may be needed but adds engineering complexity',
  ],
  finalRecommendation: 'Proceed with European expansion using a phased, gate-based approach. Launch UK operations immediately while running DACH market preparation in parallel. Invest $4.8M in Year 1 (revised upward from $4.2M for compliance). Breakeven expected at month 25-28. Execute DACH launch upon meeting validation gates: 20 UK customers and 5 qualified DACH leads within 9 months.',
  rationale: 'Both debaters converged on a phased approach with validation gates. The strategic opportunity is clear (EUR 72B market, significant penetration gaps), and the financial case is robust (47% IRR base case, 35%+ IRR even under stress). The primary risk factors (compliance costs, longer sales cycles, cultural adaptation) are manageable with proper planning and phased execution.',
};

function buildGhostDeck(): GhostDeckSlide[] {
  return [
    {
      slideNumber: 1, title: 'Executive Summary',
      headlineInsight: 'European B2B SaaS expansion represents a EUR 72B market opportunity with projected 47% IRR',
      keyPoints: [
        'European B2B SaaS market: EUR 72B, growing 18% CAGR',
        'DACH mid-market penetration gap creates significant whitespace',
        'Phased entry: UK (0-6mo) -> DACH (6-18mo) with validation gates',
        'Investment: $4.8M Year 1, breakeven month 25-28',
      ],
      supportingEvidence: ['IDC European SaaS Tracker', 'Gartner Market Forecast', 'Internal Financial Model'],
      slideType: 'executive_summary',
    },
    {
      slideNumber: 2, title: 'Market Opportunity',
      headlineInsight: 'EUR 21B DACH market has only 34% SaaS penetration vs 67% in the US',
      keyPoints: [
        'Total European B2B SaaS: EUR 72B (2025), projected EUR 152B by 2028',
        'DACH: EUR 21B | UK: EUR 18B | Nordics: EUR 9B',
        'Healthcare IT and FinTech verticals show highest growth (22-24% CAGR)',
        'Mid-market penetration gap represents ~EUR 8B addressable whitespace',
      ],
      supportingEvidence: ['IDC Cloud Tracker Q3 2025', 'Gartner European SaaS Forecast', 'Forrester B2B Buying Behavior Report'],
      workstreamSource: 'Market Opportunity Assessment',
      slideType: 'analysis',
    },
    {
      slideNumber: 3, title: 'Regulatory Navigation',
      headlineInsight: 'Compliance is table stakes, not a barrier - EU AI Act creates competitive moat for prepared entrants',
      keyPoints: [
        'GDPR compliance is baseline requirement (not differentiator)',
        'EU AI Act: most B2B SaaS classified as "limited risk" - manageable transparency obligations',
        'Data residency in EU required by 73% of enterprise buyers',
        'Recommended: Irish HoldCo + German/Netherlands subsidiaries',
      ],
      supportingEvidence: ['EU AI Act Official Text', 'GDPR Compliance Framework', 'Tax Advisory Analysis'],
      workstreamSource: 'Regulatory & Compliance Landscape',
      slideType: 'analysis',
    },
    {
      slideNumber: 4, title: 'Go-to-Market Strategy',
      headlineInsight: 'UK-first entry with parallel DACH preparation balances speed with risk management',
      keyPoints: [
        'Phase 1 (0-6mo): UK launch - English language, lower adaptation cost',
        'Phase 2 (6-18mo): DACH expansion with full localization and EU data centers',
        'Phase 3 (18-30mo): Nordics and Benelux as high-GDP, English-proficient markets',
        'Hybrid distribution: direct enterprise sales + channel partners for mid-market',
      ],
      supportingEvidence: ['GTM Playbook Analysis', 'Comparable Expansion Case Studies', 'Sales Cycle Data'],
      workstreamSource: 'Go-to-Market Strategy',
      slideType: 'analysis',
    },
    {
      slideNumber: 5, title: 'Financial Projections',
      headlineInsight: 'IRR of 47% with breakeven at month 25 - robust under stress testing',
      keyPoints: [
        'Year 1 investment: $4.8M (revised upward for compliance)',
        'Revenue projection: Y1 $1.8M -> Y2 $5.4M -> Y3 $12.1M',
        'Breakeven: month 25 (base case), month 31 (downside)',
        'NPV of $18.7M at 12% discount rate over 5-year horizon',
      ],
      supportingEvidence: ['Internal Financial Model', 'Comparable Company Benchmarks', 'Sensitivity Analysis'],
      workstreamSource: 'Financial Modeling & ROI',
      slideType: 'analysis',
    },
    {
      slideNumber: 6, title: 'Key Risks & Mitigation',
      headlineInsight: 'Three critical risks identified with mitigation strategies',
      keyPoints: [
        'Compliance cost overrun: mitigated by revised $1.1M budget + dedicated compliance officer',
        'Sales cycle length: mitigated by UK validation phase before full DACH commitment',
        'Competitive timing: mitigated by parallel DACH preparation during UK phase',
        'Currency risk: recommend EUR revenue hedging strategy',
      ],
      supportingEvidence: ['Stress Test Results', 'Cross-Workstream Conflict Analysis', 'Risk Register'],
      slideType: 'risk_assessment',
    },
    {
      slideNumber: 7, title: 'Recommendation & Next Steps',
      headlineInsight: 'Proceed with phased expansion - validation gates govern phase transitions',
      keyPoints: [
        'DECISION: Proceed with European expansion using gate-based phased approach',
        'GATE 1: 20 UK paying customers within 9 months to trigger DACH launch',
        'GATE 2: 5+ qualified DACH pipeline leads from parallel pre-selling',
        'GATE 3: Full EU AI Act and GDPR compliance certification',
        'TIMELINE: UK launch Q2 2026, DACH launch Q4 2026 (contingent on gates)',
      ],
      supportingEvidence: ['Council Debate Consensus', 'All Workstream Findings', 'Financial Stress Tests'],
      slideType: 'recommendation',
    },
  ];
}

export function buildStrategicExampleState(): StrategicStreamingState {
  const allWorkstreams = [...buildWorkstreams(), buildFinancialWorkstream()];
  const ghostDeck = buildGhostDeck();
  return {
    phase: 'concluded',
    topic: EXAMPLE_QUERY,
    intensity: 'deep',
    workstreamCount: 4,
    totalAgents: 6,
    estimatedSeconds: 180,
    workstreams: allWorkstreams,
    conflicts: buildConflicts(),
    stressTestResults: buildStressTestResults(),
    debateArguments: buildDebateArguments(),
    debateRound: 2,
    debateRounds: 2,
    mediatorConsensus: MEDIATOR_CONSENSUS,
    ghostDeck: ghostDeck,
    deliverable: {
      killSheet: {
        executiveSummary: 'European expansion is strategically warranted. A phased, gate-based approach starting with UK and expanding to DACH provides the optimal risk-return balance.',
        recommendation: 'Proceed with phased European expansion',
        confidenceLevel: 'High (86%)',
        keyFindings: ['EUR 72B market at 18% CAGR', 'DACH penetration gap creates whitespace', '$4.8M investment, 47% IRR', 'Breakeven month 25-28'],
        risks: ['Compliance cost overrun', 'Extended sales cycles', 'Competitive timing pressure', 'Currency risk'],
        opportunities: ['First-mover in DACH mid-market', 'EU AI Act compliance as moat', 'Healthcare IT and FinTech verticals', 'Platform revenue from European ecosystem'],
        nextSteps: ['Hire European GM (immediate)', 'Engage EU compliance advisory firm', 'Begin UK customer development', 'Start DACH localization sprint'],
      },
      ghostDeck: ghostDeck,
      executiveSummary: 'European B2B SaaS expansion represents a EUR 72B market opportunity. Recommended approach: phased entry starting with UK, expanding to DACH upon meeting validation gates. Projected IRR of 47% with breakeven at month 25-28.',
      totalLatencySeconds: 127.4,
    },
    competitorData: [
      {
        ticker: 'CRM',
        companyName: 'Salesforce Inc.',
        sector: 'Technology',
        industry: 'Software - Application',
        price: 285.50,
        priceFormatted: '$285.50',
        change: 3.42,
        changePercent: 1.21,
        changeFormatted: '+1.21%',
        marketCap: 280000000000,
        marketCapFormatted: '$280B',
        peRatio: 45.2,
        peFormatted: '45.2x',
        eps: 6.32,
        epsFormatted: '$6.32',
        volume: 8420000,
        avgVolume: 7850000,
        dayHigh: 287.10,
        dayLow: 282.30,
        yearHigh: 310.45,
        yearLow: 212.00,
        '52WeekRange': '$212.00 - $310.45',
        employees: 79000,
        website: 'https://www.salesforce.com',
        fetchedAt: '2026-02-06T16:00:00Z',
      },
      {
        ticker: 'HUBS',
        companyName: 'HubSpot Inc.',
        sector: 'Technology',
        industry: 'Software - Application',
        price: 642.80,
        priceFormatted: '$642.80',
        change: -5.20,
        changePercent: -0.80,
        changeFormatted: '-0.80%',
        marketCap: 33500000000,
        marketCapFormatted: '$33.5B',
        peRatio: 78.5,
        peFormatted: '78.5x',
        eps: 8.19,
        epsFormatted: '$8.19',
        volume: 1240000,
        avgVolume: 1180000,
        dayHigh: 650.00,
        dayLow: 638.50,
        yearHigh: 695.00,
        yearLow: 445.20,
        '52WeekRange': '$445.20 - $695.00',
        employees: 7600,
        website: 'https://www.hubspot.com',
        fetchedAt: '2026-02-06T16:00:00Z',
      },
      {
        ticker: 'NOW',
        companyName: 'ServiceNow Inc.',
        sector: 'Technology',
        industry: 'Software - Infrastructure',
        price: 892.40,
        priceFormatted: '$892.40',
        change: 12.60,
        changePercent: 1.43,
        changeFormatted: '+1.43%',
        marketCap: 185000000000,
        marketCapFormatted: '$185B',
        peRatio: 62.3,
        peFormatted: '62.3x',
        eps: 14.32,
        epsFormatted: '$14.32',
        volume: 2150000,
        avgVolume: 1980000,
        dayHigh: 898.00,
        dayLow: 885.20,
        yearHigh: 920.00,
        yearLow: 620.50,
        '52WeekRange': '$620.50 - $920.00',
        employees: 22000,
        website: 'https://www.servicenow.com',
        fetchedAt: '2026-02-06T16:00:00Z',
      },
    ],
    startedAt: new Date(Date.now() - 127400).toISOString(),
    completedAt: new Date().toISOString(),
  };
}

